/**
 * Capture sheet (transcript-ui `CaptureSheet`, controlled): text, dictation, photos/camera/paste;
 * target "Let Viko sort it", a project, or a live workstream.
 *
 *  - **Idea** → `POST /api/capture` (JSON, or multipart with files). The hub records the idea and
 *    returns a `brain.triage` command; this device signs it (device key, no tap: not a risky
 *    kind) and submits it, so the brain machine sorts the capture into a review card.
 *  - **Direct** → a signed `run.message` straight into the live session (attachments uploaded to
 *    the artifact origin first).
 *
 * Without R2 the hub answers 503 `storage_not_configured`; the sheet then says storage is not
 * enabled and keeps the text so it can be sent without files.
 */
import { type CaptureTarget, CaptureSheet as Sheet } from '@viko/transcript-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { StorageNotConfigured } from '../lib/api.js'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../lib/attachment-input.js'
import { type Backend, useBackend } from '../lib/backend.js'
import { isLive, useProjects, useRuns } from '../lib/queries.js'
import { useDictation } from '../lib/speech.js'
import type { BrainCommand } from '../lib/types.js'
import { revokeAll } from '../lib/uploads.js'
import { AttachmentNotices } from './AttachmentDropZone.js'
import type { CapturePrefill } from './app-ui.js'
import { errorText, useToast } from './ui.js'

interface Target extends CaptureTarget {
  projectId?: string
  workstreamId?: string
  runId?: string
  machineId?: string
}

/** Module flag: once the hub said storage is off, attachments are disabled everywhere. */
export const storage = { off: false }

export function CaptureSheet({
  open,
  prefill,
  onClose,
}: {
  open: boolean
  prefill?: CapturePrefill
  onClose: () => void
}) {
  const b = useBackend()
  const toast = useToast()
  const runs = useRuns()
  const projects = useProjects()
  const [text, setText] = useState('')
  const [mode, setMode] = useState<'idea' | 'direct'>('idea')
  const [targetId, setTargetId] = useState('auto')
  const [picker, setPicker] = useState(false)
  const [sending, setSending] = useState(false)
  const [storageOff, setStorageOff] = useState(storage.off)
  // Hub-stored idea captures are capped at 5 MB/file; direct sends use encrypted artifacts.
  const hubCapture = mode === 'idea' && !b.content
  const input = useAttachmentInput({
    storageOff,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setText((cur) => cur + t),
    ...(hubCapture
      ? { maxFileBytes: 5 * 1024 * 1024, maxFiles: 10, maxTotalBytes: 8 * 1024 * 1024 }
      : {}),
  })
  const files = input.files
  // Sealed content can't carry files on an idea capture (no run to bind them to).
  const filesBlocked = mode === 'idea' && !!b.content && files.length > 0
  const photos = useRef<HTMLInputElement>(null)
  const camera = useRef<HTMLInputElement>(null)
  const dictation = useDictation((t) =>
    setText((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t)),
  )

  // Reset only when the sheet opens or its prefill changes. `input` is read through a ref: its
  // `add` identity follows the per-mode file limits, so depending on it would reset the picked
  // target/mode the moment the user switches to Direct.
  const inputRef = useRef(input)
  inputRef.current = input
  useEffect(() => {
    if (!open) return
    setText(prefill?.text ?? '')
    inputRef.current.clear()
    if (prefill?.files?.length) {
      inputRef.current.add(prefill.files.map((f) => f.file))
      revokeAll(prefill.files)
    }
    setMode(prefill?.mode ?? 'idea')
    setTargetId(prefill?.targetId ?? 'auto')
    setPicker(false)
  }, [open, prefill])

  const targets = useMemo<Target[]>(() => {
    const out: Target[] = [{ id: 'auto', label: 'Let Viko sort it', kind: 'auto' }]
    for (const r of runs.data ?? []) {
      if (!isLive(r) && r.status !== 'idle') continue
      out.push({
        id: `run:${r.runId}`,
        label: r.workstreamName ?? r.title ?? r.runId,
        kind: 'workstream',
        ...(r.projectName ? { project: r.projectName } : {}),
        ...(r.projectId ? { projectId: r.projectId } : {}),
        ...(r.workstreamId ? { workstreamId: r.workstreamId } : {}),
        runId: r.runId,
        machineId: r.machineId,
        ...(r.agent ? { agent: r.agent } : {}),
        live: true,
      })
    }
    for (const p of projects.data ?? [])
      out.push({ id: `project:${p.id}`, label: p.name, kind: 'project', projectId: p.id })
    return out
  }, [runs.data, projects.data])

  const target = targets.find((t) => t.id === targetId) ?? targets[0]

  const close = () => {
    dictation.stop()
    input.clear()
    onClose()
  }

  const submit = async () => {
    if (!text.trim() && !files.length) return
    if (filesBlocked)
      return toast(
        'Files can’t ride on an idea capture yet. Switch to Direct and pick a live workstream, or remove them.',
        'error',
      )
    if (!input.canSend)
      return toast('Remove or fix the attachments marked with an error first.', 'error')
    setSending(true)
    input.markUploading(true)
    try {
      if (mode === 'direct' && target?.runId && target.machineId) {
        const attachments = files.length
          ? await b.uploadAttachments(files, target.runId, target.machineId)
          : undefined
        await b.dispatch('run.message', target.machineId, {
          runId: target.runId,
          text: text.trim(),
          queue: true,
          clientMessageId: crypto.randomUUID(),
          ...(attachments ? { attachments } : {}),
        })
        toast(`Sent to ${target.label}`)
      } else {
        const r = await b.api.capture({
          text: text.trim(),
          mode: 'idea',
          target:
            target?.projectId || target?.workstreamId
              ? {
                  ...(target.projectId ? { projectId: target.projectId } : {}),
                  ...(target.workstreamId ? { workstreamId: target.workstreamId } : {}),
                }
              : 'auto',
          files: files.map((f) => f.file),
          ...(prefill?.buildId ? { origin: { buildId: prefill.buildId } } : {}),
        })
        const sorted = r.command
          ? await signBrainJob(b, {
              jobId: r.jobId ?? r.idea.id,
              machineId: r.machineId ?? null,
              command: r.command,
            })
          : false
        toast(sorted ? 'Captured — Viko is sorting it' : 'Captured')
      }
      input.clear()
      setText('')
      onClose()
    } catch (e) {
      if (e instanceof StorageNotConfigured) {
        storage.off = true
        setStorageOff(true)
      }
      toast(errorText(e), 'error')
    } finally {
      input.retryable()
      input.markUploading(false)
      setSending(false)
    }
  }

  const add = input.add

  return (
    <>
      <Sheet
        open={open}
        placement="auto"
        autoFocus
        targets={targets}
        value={text}
        onTextChange={setText}
        mode={mode}
        onModeChange={setMode}
        targetId={target?.id ?? 'auto'}
        onTargetChange={setTargetId}
        pickerOpen={picker}
        onPickerChange={setPicker}
        images={input.items}
        notices={<AttachmentNotices input={input} />}
        dropProps={input.dropProps}
        dropActive={input.dragging}
        dictating={dictation.listening}
        dictationSupported={dictation.supported}
        {...(dictation.interim ? { interim: dictation.interim } : {})}
        onDictate={dictation.toggle}
        onCamera={() => camera.current?.click()}
        onPhotos={() => photos.current?.click()}
        onRemoveImage={input.remove}
        onPaste={input.onPaste}
        sending={sending}
        {...(filesBlocked
          ? {
              warning:
                'Files can’t ride on an idea capture yet (end-to-end encrypted). Switch to Direct and pick a live workstream, or remove them.',
            }
          : storageOff
            ? {
                warning: files.length
                  ? 'File storage is not enabled on this server — remove the attachments to send.'
                  : 'File storage is not enabled on this server; captures are text only for now.',
              }
            : dictation.error
              ? { warning: dictation.error }
              : {})}
        onClose={close}
        onSend={() => void submit()}
      />
      <input
        ref={photos}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          add(e.target.files)
          e.target.value = ''
        }}
      />
    </>
  )
}

const signed = new Set<string>()

/**
 * Sign a hub-prepared brain job with this device's key (no tap) and submit it. Returns false
 * when there is no brain machine yet (the hub keeps the job; `GET /api/brain/jobs`).
 */
export async function signBrainJob(b: Backend, job: BrainCommand): Promise<boolean> {
  const machineId = job.command.machineId ?? job.machineId
  if (!machineId || signed.has(job.jobId)) return false
  signed.add(job.jobId)
  try {
    await b.dispatch(job.command.kind, machineId, job.command.payload as never)
    return true
  } catch (e) {
    signed.delete(job.jobId)
    throw e
  }
}
